import { Navigate, Route, Routes } from 'react-router-dom';

import {
  AcceptInvitationPage,
  AUTH_CALLBACK_ROUTE,
  AuthCallbackPage,
  EMAIL_VERIFY_ROUTE,
  HELP_ROUTE,
  HOME_ROUTE,
  INVITATION_ACCEPT_ROUTE,
  InvitationRequestPage,
  LOGIN_ROUTE,
  LoginPage,
  ProtectedRoute,
  REGISTRATION_ROUTE,
  RegistrationPage,
  SIGN_IN_REQUEST_ROUTE,
  VerifyEmailPage
} from '@/domains/auth';
import { OnboardingPreviewPage } from '@/domains/onboarding-preview';
import { legacyRoutes } from '@/legacy-routes';
import { RoleLanding } from '@/zones/boundary';
import { HelpRoute } from '@/zones/portal/help-page';
import { portalRoutes } from '@/zones/portal/portal-routes';
import { teamRoutes } from '@/zones/team/team-routes';

/**
 * The whole address space, in the two portals' shape (`docs/superpowers/specs/2026-09-28-two-portals-design.md`,
 * §2 and §6): the front door both portals share, the Advisor Portal under `/portal`, the Team
 * Portal under `/team`, and every address the app had before, redirected.
 *
 * **The two portals are separable, as if served from two hostnames.** Each has its own route
 * module and its own shell, and neither names a route of the other; `/` is the one switch between
 * them, by role (`RoleLanding`), and the two boundary pages are the only screens that link across.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path={LOGIN_ROUTE} element={<LoginPage />} />
      {/* Where Invent's IdP sends the browser back. Anonymous, since it is how a session begins,
          and outside both zones: one sign-in serves two portals, and the account's kind decides
          where it lands. The path is the redirect URI the IdP's portal registers per origin. */}
      <Route path={AUTH_CALLBACK_ROUTE} element={<AuthCallbackPage />} />
      {/* Asking for an invitation to the IdP — anonymous, since asking is what somebody who
          cannot sign in does. */}
      <Route path={SIGN_IN_REQUEST_ROUTE} element={<InvitationRequestPage />} />

      {/* Where survey-api's address confirmation mail links (SR-213 block 6). Anonymous because
          the mail is opened wherever it is read, signed in or not, and a confirmation proves an
          address without needing its account; it grants nothing and issues no session. The
          path is the constant survey-api's `Mail:WebBaseUrl` links are built against — spelled
          differently, every mailed link would land on the catch-all below. */}
      <Route path={EMAIL_VERIFY_ROUTE} element={<VerifyEmailPage />} />

      {/* Where survey-api's point-of-contact invitation links (SR-416). Anonymous: the person it
          reached may have no account yet, and accepting is what makes one. It issues no session
          and drops none. */}
      <Route path={INVITATION_ACCEPT_ROUTE} element={<AcceptInvitationPage />} />

      {/* Self-registration (SR-213). Anonymous for the plainest reason: nobody on it has an
          account until its last step creates one. The run it walks is kept per tab and never in
          this path, which stays `/registration` from the first screen to the last — a run id
          finishes a registration for whoever holds it, and nginx logs every request line (see
          `registrationRunStorage`). A visitor with a session is sent on to the same page inside
          the Advisor Portal (`ADD_SURVEY_ROUTE`, mounted in `portalRoutes`) by the page itself:
          survey-api binds a run to whatever session starts it. */}
      <Route
        path={REGISTRATION_ROUTE}
        element={<RegistrationPage mode={{ caller: 'anonymous' }} />}
      />

      {/* The Advisor Portal's Help, anonymous too: registration's refusals point at
          it before any account exists. It chooses its own frame — the front door signed out, the
          portal's shell for an advisor, the boundary page for staff. */}
      <Route path={HELP_ROUTE} element={<HelpRoute />} />

      {/* The onboarding design study: registration and the migrated-account first login,
          clickable, over invented data and talking to nothing. Anonymous, because the process it
          draws has no account at any point before its last screen, so a reviewer of it cannot be
          asked for one either. Under `/preview/` so that the study never holds a name the product
          needs; the product links to no page under it, and the study links to the real screens
          it mirrors. */}
      <Route path="/preview/onboarding" element={<OnboardingPreviewPage />} />

      {legacyRoutes()}

      {/* Everything below the guard requires a valid session. */}
      <Route element={<ProtectedRoute />}>
        <Route path={HOME_ROUTE} element={<RoleLanding />} />
        {portalRoutes()}
        {teamRoutes()}
      </Route>

      <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
    </Routes>
  );
}
